<script setup>
    import dayjs from 'dayjs'
    import {computed} from "vue";

    const props = defineProps({
        order: {
            type: Object,
            required: true
        }
    })

    // The list filters on the status date, so both dates belong on the row rather than just the
    // placement date the timestamp shows.
    const timestampTooltip = computed(() => {
        const placed = `Placed ${dayjs(props.order.created_at).format('DD MMM YYYY HH:mm')}`

        if (!props.order.status_changed_at) {
            return placed
        }

        return `${placed} · Status changed ${dayjs(props.order.status_changed_at).format('DD MMM YYYY HH:mm')}`
    })

    const icon = computed(() => {
        switch (props.order.status) {
            case 'created': return 'exclaim-default'
            case 'active': return 'check-verified'
            case 'service-suspended': case 'service-terminated': case 'cancelled': return 'exclaim-circle'
            default: return 'cart-default'
        }
    })

</script>

<template>
    <router-link :to="`/orders/view/${order.uuid}`" :class="['order-row-item', {'--is-active':order.status === 'active'}]">
        <ah-icon class="order-status-icon" :name="icon" />
        <dl class="order-timestamp" :title="timestampTooltip">
            <dt>{{ dayjs(order.created_at).format('DD MMM') }}</dt>
            <dd>{{ dayjs(order.created_at).format('HH:mm') }}</dd>
        </dl>

        <dl class="order-meta">
            <dt>{{order.cart.client_contact.first_name + " " + order.cart.client_contact.last_name}}.</dt>
            <dd>{{ order.cart.client_contact.email_address }}</dd>
            <dd>{{ order.cart.client_contact.cell_number }}</dd></dl>

        <dl class="order-meta --has-address">
            <dt>{{order.cart.solution?.name ?? 'Unknown Package'}}</dt>
            <dd>{{ order.cart.address }}</dd>
        </dl>

        <dl class="order-meta">
            <dd>{{ order.uuid }}</dd></dl>

        <p class="order-status">{{ order.status.replace('-', ' ') }}</p>

        <div class="order-navigation"><ah-icon name="arrow-right" /></div>
    </router-link>
</template>

<style lang="scss">
    .order-row-item {
        display: grid;
        padding: 0 2rem;
        grid-template-columns: 2.4rem 8rem 22rem 1fr 28rem 18rem 3.6rem; // 1440
        align-content: center;
        gap: 2rem;
        background: #fff;
        border-radius: 2.4rem;
        height: 8rem;
        color: var(--color-grey-thunder);
        cursor: pointer;
        //transition: all .2s ease;

        > * {
            align-self: center;
            cursor: pointer;
        }

        .order-status-icon {
            fill: var(--color-theme-primary);
            //transition: fill .2s ease;
        }

        .order-timestamp {
            background: var(--color-grey-mist);
            height: 6rem;
            border-radius: 1.8rem;
            border: .1rem solid var(--color-grey-dew);
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            //transition: all .2s ease;

            dt, dd {
                @include font(1.2, null, black);
            }

            dt {
                margin-bottom: .6rem;
            }
        }

        .order-meta {
            display: flex;
            flex-direction: column;
            gap: .6rem;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            dt {
                @include font(1.4, 1.4, black);
                margin-bottom: 0;
                padding-bottom: 0;
            }

            dd {
                @include font(1.2, 1.2, book);
            }

            &.--has-address dd {
                //text-transform: capitalize;
                //overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }

        .order-status {
            text-transform: capitalize;
            background: var(--color-grey-mist);
            border: .1rem solid var(--color-grey-dew);
            text-align: center;
            @include font(1.1, 3, black);
            border-radius: 1.5rem;
            //transition: all .2s ease;
        }

        .order-assign {
            border: none;
            background: var(--color-theme-primary);
            height: 4.4rem;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 1rem;
            padding: 0 3rem;
            border-radius: 2.2rem;
            //transition: background-color .2s ease;

            .label {
                color: #fff;
                font-size: 1.4rem;
                font-weight: var(--font-weight-black);
            }

            .icon {
                fill: #fff;
            }
        }

        .order-action,
        .order-navigation {
            width: 3.6rem;
            height: 3.6rem;
            border-radius: 50%;
            background: transparent;
            fill: var(--color-grey-hazy);
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all .2s ease;
        }

        @include mobile() {
            padding: 2rem;
            display: inline-flex;
            gap: 0 2rem;
            flex-wrap: wrap;
            height: auto;

            .order-meta {
                width: 100%;
                padding: 2rem 0;

                &:nth-of-type(3) {
                    border-width: .1rem 0;
                    border-color: var(--color-grey-mist);
                    border-style: solid;
                }
            }

            .icon {
                width: 2.4rem;
            }

            .order-timestamp {
                width: calc(100% - 4.4rem);
                flex-direction: column;
                height: 5.2rem;

                dd {
                    display: none;
                }
            }

            .order-status {
                display: inline-flex;
                justify-content: center;
                padding: 0 3rem;
            }
            .order-navigation {
                display: inline-flex;
                margin-left: auto;
                .icon {
                    top: 0;
                }
            }
        }

        @include hover() {
            background: var(--color-theme-primary);
            color: #fff;

            .order-status-icon {
                fill: #fff;
            }

            .order-timestamp {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .order-status {
                background: var(--color-black-50);
                border-color: transparent;
                color: var(--color-white);
            }

            .order-assign {
                background: var(--color-black-50);
            }

            .order-action,
            .order-navigation {
                background: #fff;

                @include hover() {
                    background: var(--color-red-raspberry);
                    fill: #fff;
                }
            }
        }

        @include dark() {
            background-color: var(--color-darth-maul);
            color: #fff;

            .order-timestamp {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .order-status {
                background: var(--color-black-50);
                border-color: transparent;
                color: var(--color-white)
            }

            .order-action,
            .order-navigation {
                fill: var(--color-grey-dew);
            }

            @include hover() {
                background: var(--color-theme-primary);

                .order-action,
                .order-navigation {
                    background: var(--color-darth-maul);

                    @include hover() {
                        background: var(--color-red-raspberry);
                        fill: #fff;
                    }
                }
            }
        }

        &.--is-active {
            //grid-template-columns: 2.4rem 8rem  repeat(3, 1fr) 3.6rem;
        }
    }
</style>
